<template>
  <div class="project-page">
    <section class="project-shell">
      <div class="project-hero">
        <div class="hero-copy">
          <nav class="project-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span>Project Inventory</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">All Projects</span>
          </nav>

          <p class="hero-kicker">Project Command Center</p>
          <h1>NGO Inventory with Sharper Executive View</h1>
          <p class="hero-description">
            Gain a clear overview of NGOs, their interventions, geographic coverage, thematic areas,
            and implementation status to support coordination and decision-making'
          </p>
        </div>

        <div class="hero-metrics">
          <article class="metric-card">
            <span class="metric-label">Portfolio size</span>
            <strong class="metric-value">{{ projects.length }}</strong>
            <span class="metric-meta">Projects currently in view</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Pending</span>
            <strong class="metric-value">{{ pendingCount }}</strong>
            <span class="metric-meta">Awaiting internal review</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Approved</span>
            <strong class="metric-value">{{ approvedCount }}</strong>
            <span class="metric-meta">Cleared for publication</span>
          </article>
        </div>
      </div>

      <section class="toolbar">
        <div class="status-switcher" role="tablist" aria-label="Project status filters">
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isPending }"
            @click="changePend"
          >
            Pending Projects
            <span class="pill-count">{{ pendingCount }}</span>
          </button>
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isApproved }"
            @click="changeApprov"
          >
            Approved Projects
            <span class="pill-count">{{ approvedCount }}</span>
          </button>
          <button
            type="button"
            class="status-pill"
            :class="{ 'status-pill--active': isAll }"
            @click="changeAll"
          >
            All Projects
            <span class="pill-count">{{ projects.length }}</span>
          </button>
        </div>

        <label class="search-shell" for="project-search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            id="project-search"
            v-model.trim="search"
            type="search"
            placeholder="Search projects by name"
          />
        </label>
      </section>

      <b-row class="content-grid" no-gutters>
        <b-col cols="12" xl="9" class="content-main">
          <div v-if="loading" class="loading-state">
            <b-spinner />
            <p>Curating project inventory...</p>
          </div>

          <template v-else>
            <div class="results-header">
              <div>
                <p class="results-kicker">Curated list</p>
                <h2>{{ currentViewTitle }}</h2>
              </div>
              <div class="results-meta">
                <span
                  >{{ computedData.length }} result{{ computedData.length === 1 ? '' : 's' }}</span
                >
                <span class="results-divider"></span>
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
              </div>
            </div>

            <div v-if="ComputedProjects.length" class="project-list">
              <article
                v-for="(project, index) in ComputedProjects"
                :key="project.id || index"
                class="premium-project-card"
                @click="openProject(project.id)"
              >
                <div class="card-glow"></div>

                <div class="project-card__top">
                  <div>
                    <span class="project-index">Project {{ padIndex(index) }}</span>
                    <h3>{{ project.name }}</h3>
                  </div>

                  <span class="status-badge" :class="statusBadgeClass(getReviewStatus(project))">
                    {{ getReviewStatus(project) }}
                  </span>
                </div>

                <p class="project-summary">
                  {{ getProjectSummary(project) }}
                </p>

                <div class="project-detail-grid">
                  <div class="detail-card">
                    <span class="detail-label">Implementation window</span>
                    <strong>{{ getTimeline(project) }}</strong>
                  </div>
                  <div class="detail-card">
                    <span class="detail-label">Program areas</span>
                    <strong>{{ getProgramAreas(project) }}</strong>
                  </div>
                  <div class="detail-card">
                    <span class="detail-label">Created</span>
                    <strong>{{ formatDate(project.created_at) }}</strong>
                  </div>
                </div>

                <div class="project-card__footer">
                  <span class="footer-note">Select to review the full project record</span>
                  <button type="button" class="view-project-btn">View Project</button>
                </div>
              </article>
            </div>

            <div v-else class="empty-state">
              <img src="../../../../assets/no-data.svg" alt="No projects available" />
              <h3>No projects available</h3>
              <p>There are no projects matching the current search and filter combination.</p>
            </div>

            <div v-if="computedData.length" class="pagination-shell">
              <div class="pagination-copy">
                <p>Rows per page</p>
                <select id="project-page-size" v-model="perPage">
                  <option value="3">3</option>
                  <option value="5">5</option>
                  <option value="10">10</option>
                </select>
              </div>

              <b-pagination
                v-model="currentPage"
                :total-rows="computedData.length"
                :per-page="perPage"
                pills
                align="right"
                aria-controls="project-list"
              />
            </div>
          </template>
        </b-col>

        <!-- <b-col cols="12" xl="3" class="content-sidebar">
          <div class="sidebar-panel">
            <div class="sidebar-copy">
              <p class="sidebar-kicker">Refine portfolio</p>
              <h3>Filter inventory</h3>
              <span>Narrow the visible project list with structured requirements.</span>
            </div>
            <FilterSideBar @filterObjs="fetchDataBasedOnSelectedRequirements" />
          </div>
        </b-col> -->
      </b-row>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
// import FilterSideBar from '../../components/Shared/FilterSideBar.vue';

export default {
  // components: {
  //   FilterSideBar,
  // },

  data() {
    return {
      isAll: false,
      isApproved: false,
      isPending: true,
      currentPage: 1,
      total: 0,
      perPage: 5,
      loading: true,
      projects: [],
      tempProjects: [],
      search: '',
      programAreaMap: {},
    };
  },

  computed: {
    ...mapGetters('DATASET_STORE', ['getProject', 'getFilterData']),
    ...mapGetters('auth', ['user']),

    currentStatus() {
      if (this.isApproved) return 'Approved';
      if (this.isPending) return 'Pending';
      return 'All';
    },

    approvedCount() {
      return this.projects.filter((project) => this.getReviewStatus(project) === 'Approved').length;
    },

    pendingCount() {
      return this.projects.filter((project) => this.getReviewStatus(project) === 'Pending').length;
    },

    currentViewTitle() {
      if (this.isApproved) return 'Approved projects';
      if (this.isPending) return 'Pending projects';
      return 'All projects';
    },

    computedData() {
      return this.projects.filter((project) => {
        const status = this.getReviewStatus(project);
        const matchesStatus = this.currentStatus === 'All' || status === this.currentStatus;
        const projectName = (project.name || '').toLowerCase();
        const matchesSearch = projectName.includes(this.search.toLowerCase());

        return matchesStatus && matchesSearch;
      });
    },

    ComputedProjects() {
      return this.computedData.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.computedData.length / this.perPage));
    },
  },

  watch: {
    perPage() {
      this.currentPage = 1;
    },

    search() {
      this.currentPage = 1;
    },

    getFilterData(val) {
      this.loading = true;

      if (!val || !Array.isArray(val.status) || !val.status.length) {
        this.projects = [...this.tempProjects];
        this.loading = false;
        return;
      }

      this.projects = this.tempProjects.filter((project) =>
        val.status.includes(this.getReviewStatus(project))
      );

      this.loading = false;
    },
  },

  methods: {
    ...mapActions('DATASET_STORE', ['SETPROJECT']),

    changeAll() {
      this.isAll = true;
      this.isApproved = false;
      this.isPending = false;
      this.currentPage = 1;
    },

    changeApprov() {
      this.isAll = false;
      this.isApproved = true;
      this.isPending = false;
      this.currentPage = 1;
    },

    changePend() {
      this.isAll = false;
      this.isApproved = false;
      this.isPending = true;
      this.currentPage = 1;
    },

    async openProject(id) {
      const singleProject = this.projects.find((project) => project.id === id);

      if (!singleProject) return;

      this.SETPROJECT(singleProject);
      this.$router.push('/admin/project/sub-project');
    },

    async fetchProgramAreas() {
      try {
        const { data } = await this.$dataset.getAllProgramAreas();
        const results = data?.results || [];

        this.programAreaMap = results.reduce((acc, item) => {
          acc[item.id] = item.name;
          return acc;
        }, {});
      } catch (error) {
        this.programAreaMap = {};
      }
    },

    async fetchData() {
      this.loading = true;

      try {
        const projects = await this.$dataset.getAllProjects();
        this.projects = projects?.data?.results || [];
        this.tempProjects = [...this.projects];
        this.total = projects?.data?.count || this.projects.length;
      } finally {
        this.loading = false;
      }
    },

    async fetchDataBasedOnSelectedRequirements(data) {
      this.currentPage = 1;
      this.loading = true;

      try {
        const filtered = await this.$dataset.filteredData(data);
        this.projects = filtered.data || [];
        this.tempProjects = [...this.projects];
        this.total = this.projects.length;
      } finally {
        this.loading = false;
      }
    },

    getReviewStatus(project) {
      return project?.review_status?.value || project?.review_status || 'Pending';
    },

    getTimeline(project) {
      if (!project?.start_date && !project?.end_date) return 'Timeline not provided';
      return `${this.getYear(project.start_date)} - ${this.getYear(project.end_date)}`;
    },

    getProgramAreas(project) {
      if (!Array.isArray(project?.program_area) || !project.program_area.length) {
        return 'Not specified';
      }

      return project.program_area
        .map((program) => this.programAreaMap[program] || program)
        .join(', ');
    },

    getProjectSummary(project) {
      return (
        project?.description ||
        project?.project_type_support ||
        'A structured intervention project captured for administrative review and portfolio tracking.'
      );
    },

    getYear(date) {
      if (!date) return 'N/A';
      return moment(date).format('YYYY');
    },

    formatDate(date) {
      if (!date) return 'Date unavailable';
      return moment(date).format('DD MMM YYYY');
    },

    statusBadgeClass(status) {
      return status === 'Approved' ? 'status-badge--approved' : 'status-badge--pending';
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },
  },

  async mounted() {
    if (this.user.admin_type === 'Data Admin') {
      this.$router.push('/admin/data');
      return;
    }

    await Promise.all([this.fetchProgramAreas(), this.fetchData()]);
  },
};
</script>

<style lang="scss" scoped>
.project-page {
  min-height: 100vh;
  padding: 1rem 0.75rem 1.5rem;
  background: #ffffff;
  border-radius: 5px;
}

.project-shell {
  max-width: 1380px;
  margin: 0 auto;
}

.project-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 1fr);
  gap: 1.5rem;
  padding: 1.25rem;
  border: 1px solid rgba(20, 55, 38, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.94), rgba(246, 250, 247, 0.86)), #ffffff;
  box-shadow: 0 24px 60px rgba(25, 47, 36, 0.1);
}

.project-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #6a736c;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #123923;
  font-weight: 600;
}

.hero-kicker,
.results-kicker,
.sidebar-kicker {
  margin-bottom: 0.65rem;
  color: #1b6b40;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 14ch;
  margin-bottom: 0.85rem;
  color: #102c1d;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2rem, 4vw, 3.35rem);
  font-weight: 700;
  line-height: 1.04;
}

.hero-description {
  max-width: 60ch;
  margin-bottom: 0;
  color: #4b5b52;
  font-size: 1rem;
  line-height: 1.75;
}

.hero-metrics {
  display: grid;
  gap: 1rem;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid rgba(18, 57, 35, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(246, 248, 244, 0.88));
}

.metric-label {
  color: #5c685f;
  font-size: 0.88rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.metric-value {
  color: #102c1d;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 2rem;
  line-height: 1.1;
}

.metric-meta {
  color: #7a857e;
  font-size: 0.92rem;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(20, 55, 38, 0.08);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
}

.status-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.78rem 1rem;
  border: 1px solid rgba(28, 89, 57, 0.12);
  border-radius: 999px;
  background: rgba(248, 249, 246, 0.96);
  color: #365442;
  font-weight: 600;
  transition: all 0.2s ease;
}

.status-pill:hover,
.status-pill--active {
  background: linear-gradient(135deg, #184f31, #2c7a50);
  color: #fff;
  box-shadow: 0 16px 32px rgba(24, 79, 49, 0.2);
}

.pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: rgba(17, 40, 28, 0.08);
  font-size: 0.86rem;
}

.status-pill--active .pill-count {
  background: rgba(255, 255, 255, 0.18);
}

.search-shell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: min(100%, 340px);
  margin-bottom: 0;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(18, 57, 35, 0.08);
  border-radius: 18px;
  background: #fafbf8;
  color: #6f7c73;
}

.search-shell input {
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #183123;
}

.content-grid {
  margin-top: 1.5rem;
}

.content-main {
  padding-right: 1.25rem;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 420px;
  padding: 2rem;
  border: 1px solid rgba(20, 55, 38, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.loading-state p,
.empty-state p {
  margin-bottom: 0;
  color: #6e786f;
}

.empty-state img {
  width: min(100%, 360px);
}

.results-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.results-header h2,
.sidebar-copy h3 {
  margin-bottom: 0;
  color: #102c1d;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(1.45rem, 2.6vw, 2rem);
  font-weight: 700;
}

.results-meta {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #69776f;
  font-weight: 500;
}

.results-divider {
  width: 1px;
  height: 1rem;
  background: rgba(84, 100, 91, 0.25);
}

.project-list {
  display: grid;
  gap: 1rem;
}

.premium-project-card {
  position: relative;
  overflow: hidden;
  padding: 1.45rem;
  border: 1px solid rgba(20, 55, 38, 0.08);
  border-radius: 26px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(247, 250, 247, 0.92)), #fff;
  box-shadow: 0 20px 40px rgba(20, 39, 28, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.premium-project-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 46px rgba(20, 39, 28, 0.12);
}

.card-glow {
  position: absolute;
  top: -80px;
  right: -20px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(42, 124, 80, 0.12), transparent 68%);
  pointer-events: none;
}

.project-card__top,
.project-card__footer,
.project-detail-grid {
  position: relative;
  z-index: 1;
}

.project-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.project-index {
  display: inline-block;
  margin-bottom: 0.5rem;
  color: #779080;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.project-card__top h3 {
  margin-bottom: 0;
  color: #153424;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.35;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.status-badge--approved {
  background: rgba(39, 120, 74, 0.12);
  color: #21643f;
}

.status-badge--pending {
  background: rgba(185, 129, 29, 0.14);
  color: #8f6417;
}

.project-summary {
  position: relative;
  z-index: 1;
  margin: 1rem 0 1.2rem;
  color: #56665c;
  line-height: 1.7;
}

.project-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

.detail-card {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(20, 55, 38, 0.08);
  border-radius: 18px;
  background: rgba(248, 250, 247, 0.92);
}

.detail-label {
  display: block;
  margin-bottom: 0.35rem;
  color: #728078;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.detail-card strong {
  color: #143222;
  font-size: 0.98rem;
  line-height: 1.5;
}

.project-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(20, 55, 38, 0.08);
}

.footer-note {
  color: #7a847c;
  font-size: 0.92rem;
}

.view-project-btn {
  border: 0;
  border-radius: 999px;
  padding: 0.8rem 1.15rem;
  background: linear-gradient(135deg, #143c26, #2b7a50);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 14px 24px rgba(21, 73, 48, 0.22);
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(20, 55, 38, 0.08);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.86);
}

.pagination-copy {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.pagination-copy p {
  margin-bottom: 0;
  color: #6f7b72;
}

.pagination-copy select {
  min-width: 72px;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(20, 55, 38, 0.12);
  border-radius: 14px;
  background: #fff;
}

.content-sidebar {
  padding-left: 1.25rem;
}

.sidebar-panel {
  position: sticky;
  top: 1.5rem;
  padding: 1.2rem;
  border: 1px solid rgba(20, 55, 38, 0.08);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(244, 248, 244, 0.88)), #fff;
  box-shadow: 0 20px 38px rgba(20, 39, 28, 0.08);
}

.sidebar-copy {
  margin-bottom: 1rem;
}

.sidebar-copy span {
  color: #6a766d;
  line-height: 1.6;
}

::v-deep .pagination .page-item .page-link {
  border: 0;
  border-radius: 999px;
  margin: 0 0.2rem;
  color: #194329;
  background: #edf2ec;
}

::v-deep .pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #184f31, #2c7a50);
  color: #fff;
  box-shadow: 0 10px 20px rgba(24, 79, 49, 0.18);
}

@media (max-width: 1199px) {
  .content-main,
  .content-sidebar {
    padding-right: 0;
    padding-left: 0;
  }

  .content-sidebar {
    margin-top: 1.5rem;
  }

  .sidebar-panel {
    position: static;
  }
}

@media (max-width: 991px) {
  .project-page {
    padding-top: 5.5rem;
  }

  .project-hero,
  .project-detail-grid {
    grid-template-columns: 1fr;
  }

  .toolbar,
  .results-header,
  .pagination-shell,
  .project-card__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .results-meta {
    justify-content: space-between;
  }

  .search-shell {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .project-page {
    padding: 1.5rem 0.2rem 0.6rem;
  }

  .project-hero,
  .toolbar,
  .premium-project-card,
  .sidebar-panel,
  .pagination-shell,
  .loading-state,
  .empty-state {
    border-radius: 22px;
  }

  .project-card__top {
    flex-direction: column;
  }

  .view-project-btn {
    width: 100%;
  }

  .project-hero,
  .toolbar,
  .pagination-shell,
  .loading-state,
  .empty-state {
    padding: 0.65rem;
  }

  .premium-project-card {
    padding: 0.8rem;
  }

  .project-breadcrumb {
    margin-bottom: 0.55rem;
    gap: 0.25rem;
    font-size: 0.82rem;
  }

  .hero-kicker,
  .results-kicker,
  .sidebar-kicker {
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
  }

  .hero-copy h1 {
    margin-bottom: 0.45rem;
    font-size: 1.8rem;
  }

  .project-summary {
    margin: 0.65rem 0 0.8rem;
  }

  .hero-description {
    font-size: 0.92rem;
    line-height: 1.55;
  }

  .metric-card,
  .detail-card {
    padding: 0.8rem;
  }

  .status-pill {
    padding: 0.65rem 0.85rem;
  }

  .search-shell {
    padding: 0.7rem 0.85rem;
  }
}

@media (max-width: 600px) {
  .project-page {
    padding-top: 0.85rem;
  }

  .project-hero,
  .toolbar {
    gap: 0.55rem;
  }

  .project-hero {
    padding: 0.55rem;
  }
}
</style>
